import { useState, useCallback, useRef } from "react";
import { FiInfo, FiTrendingUp } from "react-icons/fi";
import { cn } from "utils/helpers";
import Modal from "newui/components/ui/Modal/Modal";
import { Button } from "newui/components/Button";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import { useSupplierScoring } from "../../../hooks/useSupplierScoring";
import { useScoringInstructions } from "../../../hooks/useScoringInstructions";
import { ScoringModalHeader } from "./ScoringModalHeader";
import { CriterionScoringRow } from "./CriterionScoringRow";
import { ScoringInstructionsPanel } from "./ScoringInstructionsPanel";
import { AutoSaveIndicator } from "../../../../../../components/ui/AutoSave/AutoSaveIndicator";
import type { SupplierEvaluationSummary, UnsavedChangesAction } from "../../../types/scoring.type";
import { useAutoSave } from "newui/hooks/useAutoSave";

interface ScoringModalProps {
	evaluatorId?: number;
	isOpen: boolean;
	readOnly?: boolean;
	rfxId: number;
	selectedInviteId: number;
	suppliers: SupplierEvaluationSummary[];
	onClose: () => void;
	onScoringComplete: () => void;
	onSupplierChange: (inviteId: number) => void;
}

export const ScoringModal: React.FC<ScoringModalProps> = ({
	isOpen,
	rfxId,
	suppliers,
	selectedInviteId,
	evaluatorId,
	readOnly = false,
	onClose,
	onSupplierChange,
	onScoringComplete,
}) => {
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [instructionsPanelOpen, setInstructionsPanelOpen] = useState(false);
	const [focusedScore, setFocusedScore] = useState<number | null>(null);
	const [focusedElement, setFocusedElement] = useState<HTMLDivElement | null>(null);
	const [saveError, setSaveError] = useState<string | null>(null);
	const pendingAction = useRef<(() => void) | null>(null);
	const contentRef = useRef<HTMLDivElement>(null);

	const {
		scoringDetail,
		isLoading: isLoadingScores,
		isDirty,
		lastSavedAt: scoringLastSavedAt,
		percentComplete,
		getDirtyCriteriaIds,
		loadSupplierScores,
		updateCriterionScore,
		saveCriterion,
	} = useSupplierScoring({ rfxId, evaluatorId });

	const { instructions, isLoading: isLoadingInstructions } = useScoringInstructions({
		rfxId,
	});

	const maxScore = instructions?.totalScore ?? 10;

	const saveDirtyFields = useCallback(async () => {
		const dirtyIds = getDirtyCriteriaIds();
		if (dirtyIds.length === 0) {
			return;
		}
		setSaveError(null);
		for (const criteriaId of dirtyIds) {
			const success = await saveCriterion(criteriaId);
			if (!success) {
				setSaveError("Failed to save some changes");
				return;
			}
		}
		// Refresh supplier progress in background after successful save
		onScoringComplete();
	}, [getDirtyCriteriaIds, saveCriterion, onScoringComplete]);

	const { isSaving, lastSavedAt } = useAutoSave({
		isDirty,
		onSave: saveDirtyFields,
		debounceMs: 2500,
	});

	const handleSupplierChange = useCallback(
		(inviteId: number) => {
			if (isDirty) {
				pendingAction.current = () => {
					onSupplierChange(inviteId);
					loadSupplierScores(inviteId);
				};
				setShowConfirmDialog(true);
				return;
			}
			onSupplierChange(inviteId);
			loadSupplierScores(inviteId);
		},
		[isDirty, onSupplierChange, loadSupplierScores]
	);

	const handleClose = useCallback(() => {
		if (isDirty) {
			pendingAction.current = () => {
				onScoringComplete();
				onClose();
			};
			setShowConfirmDialog(true);
			return;
		}
		onScoringComplete();
		onClose();
	}, [isDirty, onClose, onScoringComplete]);

	const handleConfirmAction = useCallback(
		async (action: UnsavedChangesAction) => {
			setShowConfirmDialog(false);
			if (action === "cancel") {
				pendingAction.current = null;
				return;
			}
			if (action === "save") {
				await saveDirtyFields();
			}
			if (pendingAction.current) {
				pendingAction.current();
				pendingAction.current = null;
			}
		},
		[saveDirtyFields]
	);

	const handleScoreChange = useCallback(
		(rfxCriteriaId: number, score: number | null) => {
			updateCriterionScore(rfxCriteriaId, "score", score);
			setFocusedScore(score);
		},
		[updateCriterionScore]
	);

	const handleCommentChange = useCallback(
		(rfxCriteriaId: number, comment: string | null) => {
			updateCriterionScore(rfxCriteriaId, "comment", comment);
		},
		[updateCriterionScore]
	);

	const handleRowFocus = useCallback(
		(rfxCriteriaId: number, element: HTMLDivElement) => {
			setFocusedElement(element);
			// Update focusedScore to the current score for this criterion
			if (scoringDetail) {
				const criterion = scoringDetail.scores.find((s) => s.rfxCriteriaId === rfxCriteriaId);
				if (criterion) {
					setFocusedScore(criterion.score);
				}
			}
			// Auto-show the guide on desktop when user focuses a row
			if (window.innerWidth >= 768) {
				setInstructionsPanelOpen(true);
			}
		},
		[scoringDetail]
	);

	// Load scores when modal opens with a selected supplier
	const prevInviteId = useRef<number | null>(null);
	if (isOpen && selectedInviteId !== prevInviteId.current) {
		prevInviteId.current = selectedInviteId;
		loadSupplierScores(selectedInviteId);
	}

	if (!isOpen) {
		return null;
	}

	const selectedSupplier = suppliers.find((s) => s.inviteId === selectedInviteId);
	const modalTitle = selectedSupplier
		? `Score Supplier - ${selectedSupplier.supplierName}`
		: "Score Supplier";

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={handleClose}
				title={modalTitle}
				titleColor="blue.800"
				customIcon={<FiTrendingUp size={30} className="fill-white stroke-blue-700" />}
				description={
					<div className="ml-8">
						<AutoSaveIndicator
							isSaving={isSaving}
							lastSavedAt={lastSavedAt ?? scoringLastSavedAt}
							error={saveError}
							size="lg"
						/>
					</div>
				}
				wrapDescription
				size="full"
				closeOnEsc={!isDirty}
				closeOnOverlayClick={false}
				customFooter={
					<div className="flex items-center justify-between w-full px-4">
						<div className="flex items-center gap-3">
							<button
								type="button"
								onClick={() => setInstructionsPanelOpen((prev) => !prev)}
								className={cn(
									"md:hidden flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors",
									instructionsPanelOpen
										? "bg-blue-100 text-blue-700 border border-blue-300"
										: "bg-blue-50 text-blue-600 border border-blue-200"
								)}
								data-testid="toggle-instructions-btn-mobile"
							>
								<FiInfo size={14} />
								{instructionsPanelOpen ? "Hide Guide" : "Guide"}
							</button>
						</div>
						<Button
							btntype="primary"
							size="xs"
							onClick={handleClose}
							data-testid="scoring-modal-close-btn"
						>
							Close
						</Button>
					</div>
				}
			>
				<div
					ref={contentRef}
					className="relative space-y-6 p-4"
					data-testid="scoring-modal-content"
				>
					<div className="flex items-center justify-between">
						<ScoringModalHeader
							suppliers={suppliers}
							selectedInviteId={selectedInviteId}
							percentComplete={percentComplete}
							onSupplierChange={handleSupplierChange}
							rfxId={rfxId}
							readOnly={readOnly}
						/>
						<button
							type="button"
							onClick={() => setInstructionsPanelOpen((prev) => !prev)}
							className={cn(
								"hidden md:flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors shrink-0",
								instructionsPanelOpen
									? "bg-blue-100 text-blue-700 border border-blue-300 hover:bg-blue-200"
									: "bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100"
							)}
							data-testid="toggle-instructions-btn"
						>
							<FiInfo size={16} />
							{instructionsPanelOpen ? "Hide Guide" : "Scoring Guide"}
						</button>
					</div>

					{isLoadingScores && (
						<div className="py-8 text-sm text-gray-400" data-testid="scoring-loading">
							<LoadingSpinner label="Loading scores" />
						</div>
					)}

					{!isLoadingScores && scoringDetail && (
						<div className="space-y-4" data-testid="scoring-grid">
							{scoringDetail.scores.map((criterion) => (
								<CriterionScoringRow
									key={criterion.rfxCriteriaId}
									criterion={criterion}
									maxScore={maxScore}
									readOnly={readOnly}
									onScoreChange={handleScoreChange}
									onCommentChange={handleCommentChange}
									onFocus={handleRowFocus}
								/>
							))}
						</div>
					)}

					{!isLoadingScores && !scoringDetail && (
						<div className="text-center py-8 text-sm text-gray-400" data-testid="scoring-empty">
							No scoring data available for this supplier.
						</div>
					)}

					<ScoringInstructionsPanel
						instructions={instructions}
						isLoading={isLoadingInstructions}
						activeScore={focusedScore}
						isOpen={instructionsPanelOpen}
						onToggle={() => setInstructionsPanelOpen(false)}
						anchorElement={focusedElement}
						containerElement={contentRef.current}
					/>
				</div>
			</Modal>

			{showConfirmDialog && (
				<Modal
					isOpen={showConfirmDialog}
					onClose={() => handleConfirmAction("cancel")}
					title="Unsaved Changes"
					size="md"
					iconType="warning"
					modalType="alert"
					closeOnEsc
					closeOnOverlayClick
					customFooter={
						<div className="flex w-full justify-center gap-3">
							<button
								type="button"
								onClick={() => handleConfirmAction("cancel")}
								className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={() => handleConfirmAction("save")}
								className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
							>
								Save &amp; Continue
							</button>
						</div>
					}
				>
					<p className="text-base text-gray-600 text-center">
						You have unsaved scoring changes.
						<br /><br />
						Would you like to save them before continuing?
					</p>
				</Modal>
			)}
		</>
	);
};
